/**
 * Copy slots — the named pieces of text a city is allowed to say differently.
 *
 * Plain English: the words on the page are DATA, the same way the palette is.
 * A hero that reads "Tampa Bay Networking Calendar" written straight into the
 * component is a sentence only one city can ever say. Every other city then
 * needs either a fork of the component or a find-and-replace, and both are how
 * a platform quietly becomes N codebases.
 *
 * So each such sentence gets an id, a default, and a note saying what it is
 * for. A component asks for the id; the city supplies an override or gets the
 * default. Nothing in a component spells a city's words out.
 *
 * Placeholders, filled from the city row:
 *   {city}  → 'Tampa Bay'                     (cities.display_name)
 *   {site}  → 'Tampa Bay Networking Calendar' (cities.site_name)
 *   {areas} → 'Tampa · St. Pete · Clearwater' (the city's approved areas)
 *
 * Today every city takes the default, because the per-city copy table is part
 * of the blueprint engine and does not exist yet. The seam is what matters:
 * when that table arrives it fills `overrides` and nothing else changes.
 *
 * `scripts/check-slots.mjs` fails the build when a component asks for a slot
 * that is not declared here, and when a slot declared here is never used.
 */

export interface CopySlot {
  /** Dotted id: page.region.thing. */
  id: string;
  /** What this text is for, so whoever rewrites it knows what they are aiming at. */
  purpose: string;
  /** What every city says until one says otherwise. */
  default: string;
}

export const COPY_SLOTS: readonly CopySlot[] = [
  {
    id: 'home.hero.eyebrow',
    purpose: 'The small line above the hero title — the places this calendar covers.',
    default: '{areas}',
  },
  {
    id: 'home.hero.title',
    purpose:
      'The biggest words on the site. The word wrapped in the accent colour is marked with *asterisks*.',
    default: '{city} *Networking* Calendar',
  },
  {
    id: 'home.hero.sub',
    purpose: 'One sentence under the hero title saying what the site is.',
    default: 'Every networking event in {city} — one place, always current.',
  },
  {
    id: 'home.hero.cta',
    purpose: 'The button in the hero that starts a listing.',
    default: 'List your event',
  },
  {
    id: 'home.empty.dayOne.title',
    purpose:
      'Headline when the city has published nothing at all. Must read as new, never as broken.',
    default: 'The {city} calendar is just opening.',
  },
  {
    id: 'home.empty.dayOne.body',
    purpose: 'The explanation under that headline — why it is empty and what to do.',
    default:
      'Nothing is listed yet — which means the first events here get the whole page. Listings are free and take about two minutes.',
  },
  {
    id: 'home.empty.dayOne.cta',
    purpose: 'The button on a day-one empty calendar.',
    default: 'List the first event — free',
  },
  {
    id: 'home.empty.filtered.title',
    purpose: 'Headline when a filter matched nothing.',
    default: 'Nothing matches these filters yet.',
  },
  {
    id: 'home.empty.window.title',
    purpose: 'Headline when the chosen week or month happens to be quiet.',
    default: 'Nothing listed for this stretch yet.',
  },
  {
    id: 'home.empty.body',
    purpose: 'The line under either non-day-one empty state.',
    default: 'This space is free, and it’s where {city} looks first.',
  },
  {
    id: 'home.empty.cta',
    purpose: 'The button on a non-day-one empty calendar.',
    default: 'List your event — free',
  },
  {
    id: 'footer.rights',
    purpose: 'The copyright line. The year is added by the footer.',
    default: '{site}. Free community listings for business networking across {city}.',
  },
] as const;

export type CopySlotId = (typeof COPY_SLOTS)[number]['id'];

export interface CopyVars {
  city: string;
  site: string;
  areas: string;
}

const BY_ID = new Map(COPY_SLOTS.map((s) => [s.id, s]));

/**
 * Resolve one slot.
 *
 * An unknown id throws rather than rendering empty: a blank hero is a bug you
 * find in production, an exception is one you find in the build.
 */
export function resolveCopySlot(
  id: string,
  vars: CopyVars,
  overrides: Readonly<Record<string, string>> = {}
): string {
  const slot = BY_ID.get(id);
  if (!slot) throw new Error(`copy slot "${id}" is not declared in src/shared/copy-slots.ts`);
  const text = overrides[id] ?? slot.default;
  return text
    .replace(/\{city\}/g, vars.city)
    .replace(/\{site\}/g, vars.site)
    .replace(/\{areas\}/g, vars.areas);
}
